import React, { useState } from 'react';
import SelectionCard from './SelectionCard';
import { cn } from '../../lib/utils';

/**
 * DomainCardPicker - Reusable domain card selection with multiclass support
 *
 * Implements SRD rule: Multiclass cards limited to half character level
 *
 * @param {Object} character - Character state
 * @param {Object} allData - Game data
 * @param {number} maxLevel - Maximum card level allowed
 * @param {number} maxCards - Maximum number of cards to select
 * @param {Array} selectedCards - Currently selected card IDs
 * @param {Function} onToggleCard - Callback when card is toggled
 * @param {string} multiclassDomain - If character has multiclassed, the multiclass domain
 */
function DomainCardPicker({
  character,
  allData,
  maxLevel,
  maxCards = 1,
  selectedCards = [],
  onToggleCard,
  multiclassDomain = null
}) {
  const domainCards = allData.domainCards || {};
  const [selectedDomain, setSelectedDomain] = useState('all');

  // Get character's available domains
  const classDomains = allData.classes?.[character.class]?.domains || [];
  const availableDomains = multiclassDomain
    ? [...classDomains, multiclassDomain]
    : classDomains;

  // Filter cards with multiclass restriction
  const availableCards = Object.entries(domainCards).filter(([id, card]) => {
    // Check if card is from available domains
    if (!availableDomains.includes(card.domain)) return false;

    // Check if already in loadout or vault
    const alreadyOwned = character.loadout?.includes(id) || character.vault?.includes(id);
    if (alreadyOwned && !selectedCards.includes(id)) return false;

    // Apply level restriction
    if (multiclassDomain && card.domain === multiclassDomain) {
      // SRD Rule: Multiclass domain cards limited to half level (rounded up)
      const multiclassMaxLevel = Math.ceil(character.level / 2);
      return card.level <= multiclassMaxLevel;
    } else {
      // Primary class domain - use full level
      return card.level <= maxLevel;
    }
  });

  // Get unique domains from available cards
  const uniqueDomains = [...new Set(availableCards.map(([, card]) => card.domain))];

  // Filter by selected domain filter
  const filteredCards = selectedDomain === 'all'
    ? availableCards
    : availableCards.filter(([, card]) => card.domain === selectedDomain);

  const selectedCount = selectedCards.length;

  return (
    <div className="space-y-4">
      {/* Domain filter */}
      {uniqueDomains.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedDomain('all')}
            className={cn(
              "px-3 py-1.5 rounded text-small font-medium transition-all duration-200 border",
              selectedDomain === 'all'
                ? "bg-red-900/30 text-red-400 border-red-700"
                : "bg-gray-800 text-gray-100 border-gray-600 hover:text-red-400 hover:bg-red-900/30 hover:border-red-700"
            )}
          >
            All Domains
          </button>
          {uniqueDomains.map(domain => {
            // Check if this is the multiclass domain
            const isMulticlass = domain === multiclassDomain;
            const maxLevelForDomain = isMulticlass
              ? Math.ceil(character.level / 2)
              : maxLevel;

            return (
              <button
                key={domain}
                onClick={() => setSelectedDomain(domain)}
                className={cn(
                  "px-3 py-1.5 rounded text-small font-medium transition-all duration-200 capitalize border",
                  selectedDomain === domain
                    ? "bg-red-900/30 text-red-400 border-red-700"
                    : "bg-gray-800 text-gray-100 border-gray-600 hover:text-red-400 hover:bg-red-900/30 hover:border-red-700"
                )}
              >
                {domain}
                {isMulticlass && (
                  <span className="ml-1 text-tiny opacity-75">
                    (up to L{maxLevelForDomain})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Selection counter */}
      {maxCards > 1 && (
        <p className="text-gray-400 text-small">
          Selected: <span className={cn(
            "font-bold",
            selectedCount === maxCards ? "text-green-400" : "text-yellow-400"
          )}>{selectedCount}/{maxCards}</span>
        </p>
      )}

      {/* Cards grid */}
      {filteredCards.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {filteredCards.map(([id, card]) => {
            const isSelected = selectedCards.includes(id);
            const canSelect = isSelected || selectedCount < maxCards;
            const isMulticlass = card.domain === multiclassDomain;

            return (
              <SelectionCard
                key={id}
                title={card.name}
                subtitle={
                  <span>
                    {card.domain} | Level {card.level}
                    {isMulticlass && (
                      <span className="ml-2 text-tiny text-yellow-400">
                        (Multiclass)
                      </span>
                    )}
                  </span>
                }
                description={card.effect || card.text || ''}
                isSelected={isSelected}
                onClick={() => canSelect && onToggleCard(id)}
                showDetails={true}
              />
            );
          })}
        </div>
      ) : (
        <p className="text-gray-400 text-center py-8">
          No cards available for this filter.
        </p>
      )}
    </div>
  );
}

export default DomainCardPicker;
